import { Component } from '@theme/component';

/** Scroll distance (px) past which the tall banner shrinks. */
const SHRINK_AFTER = 48;

/** Scroll distance (px) under which it grows back. Lower than SHRINK_AFTER so the state can't flicker. */
const GROW_BEFORE = 8;

/**
 * The tall promo banner. Once the page is scrolled it shrinks to the slim banner, by setting [data-compact];
 * CSS handles the size change and keeps it pinned under the header.
 *
 * While it shrinks, `--promo-banner-shrink` on the body holds how far it has shrunk (updated every frame of the
 * transition). The banner's section adds that to the space below it and the first hero adds it to its height
 * offset, so the page below keeps its place: otherwise the content jumps up, and the browser's scroll anchoring
 * scrolls back to the top, where the banner grows again.
 *
 * @extends Component
 */
class PromoBannerComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} */
  #frame = 0;

  /** The banner's height while tall. @type {number} */
  #tallHeight = 0;

  /** Whether the banner is animating back to full size. @type {boolean} */
  #growing = false;

  /** @type {number} */
  #resizeTimeout = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    window.addEventListener('scroll', this.#handleScroll, { passive: true, signal: this.#abortController.signal });
    this.addEventListener(
      'transitionend',
      (event) => {
        if (event.propertyName !== 'min-height') return;
        this.#growing = false;
        this.#endResize();
      },
      { signal: this.#abortController.signal }
    );

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      const height = entry?.borderBoxSize[0]?.blockSize;
      if (height !== undefined) this.#trackHeight(height);
    });
    this.#resizeObserver.observe(this);

    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#abortController = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#endResize();
    document.body.style.removeProperty('--promo-banner-shrink');
  }

  #handleScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update() {
    const scrolled = window.scrollY;
    const compact = this.hasAttribute('data-compact');

    if (!compact && scrolled > SHRINK_AFTER) {
      this.#tallHeight = this.getBoundingClientRect().height;
      this.#startResize();
      this.setAttribute('data-compact', '');
    } else if (compact && scrolled < GROW_BEFORE) {
      this.#growing = true;
      this.#startResize();
      this.removeAttribute('data-compact');
    }
  }

  /**
   * Turns off the browser's scroll anchoring while the banner changes size. The space below it is updated a
   * frame after each size change, and anchoring would scroll the page to cover that gap.
   */
  #startResize() {
    document.documentElement.classList.add('promo-banner-resizing');
    clearTimeout(this.#resizeTimeout);
    // A fallback for when no transition runs (reduced motion) or transitionend doesn't arrive.
    this.#resizeTimeout = setTimeout(() => this.#endResize(), 1000);
  }

  #endResize() {
    clearTimeout(this.#resizeTimeout);
    this.#resizeTimeout = 0;
    document.documentElement.classList.remove('promo-banner-resizing');
  }

  /** @param {number} height */
  #trackHeight(height) {
    // At full size the banner sets the height to make up for, which changes with the width (links wrap, and
    // phones never get the tall band). While it grows back, keep making up the difference.
    if (!this.hasAttribute('data-compact') && !(this.#growing && height < this.#tallHeight)) {
      this.#growing = false;
      this.#tallHeight = height;
    }

    const shrink = Math.max(0, Math.round(this.#tallHeight - height));
    document.body.style.setProperty('--promo-banner-shrink', `${shrink}px`);
  }
}

if (!customElements.get('promo-banner-component')) {
  customElements.define('promo-banner-component', PromoBannerComponent);
}

/** Scroll distance (px) after opening the phone bar's panel that closes it again. */
const CLOSE_AFTER_SCROLL = 40;

/**
 * @typedef {Object} PromoBannerStripRefs
 * @property {HTMLButtonElement} toggle - Opens and closes the panel
 * @property {HTMLElement} panel - The banner's lines and links
 */

/**
 * Phones: a slim bar pinned under the header once the section's banner has scrolled away, with a button that
 * opens the banner's lines and links over the page. Scrolling, Escape, a tap outside or Show less closes the
 * panel. CSS hides the bar from desktop.
 *
 * @extends {Component<PromoBannerStripRefs>}
 */
class PromoBannerStrip extends Component {
  requiredRefs = ['toggle', 'panel'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** Listeners that only apply while the panel is open. @type {AbortController | null} */
  #openController = null;

  /** @type {number} */
  #openScrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    const banner = this.closest('.shopify-section')?.querySelector('.promo-banner');
    if (!banner) return;

    // The bar shows once the banner has scrolled up behind the header.
    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < headerHeight;
        this.toggleAttribute('data-visible', scrolledPast);
        if (!scrolledPast) this.collapse(false);
      },
      { rootMargin: `-${Math.round(headerHeight)}px 0px 0px 0px` }
    );
    this.#observer.observe(banner);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = null;
    this.#openController?.abort();
    this.#openController = null;
  }

  /** Opens or closes the panel. */
  toggle() {
    if (this.hasAttribute('data-open')) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  /** Opens the panel over the page and moves focus into it. */
  expand() {
    if (this.hasAttribute('data-open')) return;

    this.setAttribute('data-open', '');
    this.refs.toggle.setAttribute('aria-expanded', 'true');
    this.refs.panel.focus({ preventScroll: true });
    this.#openScrollY = window.scrollY;

    this.#openController?.abort();
    this.#openController = new AbortController();
    const { signal } = this.#openController;

    window.addEventListener(
      'scroll',
      () => {
        if (Math.abs(window.scrollY - this.#openScrollY) > CLOSE_AFTER_SCROLL) this.collapse(false);
      },
      { passive: true, signal }
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') this.collapse();
      },
      { signal }
    );
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (event.target instanceof Node && !this.contains(event.target)) this.collapse(false);
      },
      { signal }
    );
  }

  /**
   * Closes the panel back to the bar.
   * @param {boolean | Event} [restoreFocus] - Whether to return focus to the bar's button; true when called
   *   from Show less or Escape, false when the page scrolled or was tapped elsewhere.
   */
  collapse(restoreFocus = true) {
    if (!this.hasAttribute('data-open')) return;

    this.#openController?.abort();
    this.#openController = null;
    this.removeAttribute('data-open');
    this.refs.toggle.setAttribute('aria-expanded', 'false');
    if (restoreFocus !== false && this.contains(document.activeElement)) this.refs.toggle.focus();
  }
}

if (!customElements.get('promo-banner-strip')) {
  customElements.define('promo-banner-strip', PromoBannerStrip);
}
